// Секцията „Взаимозамени" вдясно от таблицата на работния лист. Заменя стария
// модал (0.0.152): липса при един продукт се покрива от СВОБОДНИЯ ИЗЛИШЪК на
// друг от същата група взаимозаменяеми, така че и двата излизат на нула.
// Показва се само когато има какво да се покрие; иначе таблицата е 100%.
// На десктоп стои вдясно от продуктите, на таблет — над тях в цяла ширина.
//
// Тук е САМО изгледът — сметките (предложения, покритие, ефект върху редовете)
// са в `stocktakeWorksheetShared.tsx`, защото ги ползва и таблицата: „Замени"
// нетира разликите на двата реда ВЕДНАГА. Самото движение „Замяна" се записва
// на бекенда при запис на инвентаризацията (дете ѝ, изпълнява се след нея, за
// да изпише по FIFO тъкмо установения излишък на донора).
import { Select } from "antd";
import { SwapOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  DIFF_EPSILON,
  appliedDonorOf,
  donorOf,
  formatQtyInput,
  substitutionAvailability,
  type SubstitutionProposal,
} from "./stocktakeWorksheetShared";

type Props = {
  proposals: SubstitutionProposal[];
  // получател → избран донор (наличието на ключ = двойката е приложена)
  appliedDonorByRecipient: Map<number, number>;
  // получател → донор, избран в падащото меню (и преди прилагане)
  selectedDonorByRecipient: Map<number, number>;
  onSelectDonor: (recipientProductId: number, donorProductId: number) => void;
  onApply: (recipientProductId: number) => void;
  onRevert: (recipientProductId: number) => void;
  // „stacked" — над продуктите в цяла ширина (таблет); иначе колона вдясно
  layout?: "side" | "stacked";
};

export const StocktakeSubstitutionsPanel = ({
  proposals,
  appliedDonorByRecipient,
  selectedDonorByRecipient,
  onSelectDonor,
  onApply,
  onRevert,
  layout = "side",
}: Props) => {
  const { t } = useTranslation();
  const { donorRemaining } = substitutionAvailability(
    proposals,
    appliedDonorByRecipient,
  );
  return (
    <aside
      className={`flex shrink-0 flex-col overflow-hidden rounded-xl border border-shade-2 bg-panel ${
        layout === "stacked" ? "max-h-[320px] w-full" : "w-[320px]"
      }`}
    >
      <div className="flex shrink-0 items-center gap-2.5 border-b border-shade-2 bg-fill px-3.5 py-2.5">
        <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg bg-brand-tint text-brand-deep-ink">
          <SwapOutlined className="text-[13px]" />
        </span>
        <span className="text-[13.5px] font-bold leading-[normal] text-ink">
          {t("inventoryPage.subPanelTitle")}
        </span>
        <span className="ml-auto whitespace-nowrap rounded-full border border-brand-tint2 bg-brand-tint px-[9px] pb-[3px] pt-[2px] text-[11px] font-semibold leading-[normal] text-brand-deep-ink">
          {t("inventoryPage.subPanelPairs", { count: proposals.length })}
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3.5">
        <div
          className={
            layout === "stacked"
              ? "grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-2.5"
              : "flex flex-col gap-2.5"
          }
        >
          {/* Приложените замени изяждат от излишъка на донорите си — оттук се
              вижда какво още е свободно (и кои двойки вече не са възможни) */}
          {proposals.map((proposal) => {
            const appliedDonor = appliedDonorOf(
              proposal,
              appliedDonorByRecipient,
            );
            const applied = !!appliedDonor;
            const donor =
              appliedDonor ??
              donorOf(
                proposal,
                selectedDonorByRecipient.get(proposal.recipientProductId),
              );
            // Свободният излишък на донора СЛЕД вече приложените замени: един
            // донор с 4 кг не стига за две липси по 3 кг, затова втората двойка
            // е невъзможна и стои изключена.
            const donorAvailable = donor
              ? (donorRemaining.get(donor.productId) ?? 0)
              : 0;
            const exhausted = !applied && donorAvailable <= DIFF_EPSILON;
            return (
              <div
                key={proposal.recipientProductId}
                title={
                  exhausted ? t("inventoryPage.subNoSurplusLeft") : undefined
                }
                className={`rounded-[10px] border p-2.5 ${
                  applied
                    ? "border-brand-tint2 bg-panel"
                    : exhausted
                      ? "border-shade-2 bg-fill opacity-55"
                      : "border-shade-3 bg-panel"
                }`}
              >
                {/* Липса ← излишък */}
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-[12.5px] font-semibold leading-[normal] text-ink">
                        {proposal.recipientName}
                      </span>
                      <span className="whitespace-nowrap font-sheet text-[12px] font-bold leading-[normal] text-danger-ink">
                        -
                        {formatQtyInput(
                          proposal.shortageQty,
                          proposal.recipientDecimals,
                        )}{" "}
                        {proposal.recipientUnit}
                      </span>
                    </div>
                    <div className="mt-1 flex items-baseline justify-between gap-2">
                      <span className="truncate text-[12.5px] font-semibold leading-[normal] text-ink">
                        {donor?.name ?? "—"}
                      </span>
                      {/* при неприложена двойка показваме СВОБОДНИЯ излишък */}
                      <span
                        className={`whitespace-nowrap font-sheet text-[12px] font-bold leading-[normal] ${
                          exhausted ? "text-muted" : "text-ok-deep"
                        }`}
                      >
                        +
                        {formatQtyInput(
                          applied ? (donor?.surplusQty ?? 0) : donorAvailable,
                          donor?.decimals ?? 3,
                        )}{" "}
                        {donor?.unit ?? ""}
                      </span>
                    </div>
                  </div>
                  <SwapOutlined className="shrink-0 text-[13px] text-ghost" />
                </div>

                {/* Избор на донор, когато групата предлага повече от един */}
                {!applied && proposal.donors.length > 1 && (
                  <Select
                    value={donor?.productId}
                    onChange={(value) =>
                      onSelectDonor(
                        proposal.recipientProductId,
                        value as number,
                      )
                    }
                    // изчерпаният донор не се избира — няма с какво да покрие
                    options={proposal.donors.map((d) => {
                      const available = donorRemaining.get(d.productId) ?? 0;
                      return {
                        value: d.productId,
                        disabled: available <= DIFF_EPSILON,
                        label: `${d.name} · +${formatQtyInput(available, d.decimals)} ${d.unit}`,
                      };
                    })}
                    size="small"
                    className="mt-2 w-full"
                  />
                )}

                <div className="mt-2 flex items-center gap-2 border-t border-shade-2 pt-2">
                  <span className="text-[10.5px] leading-[normal] text-muted">
                    {t("inventoryPage.subCoefficient")}{" "}
                    <b className="font-sheet font-semibold text-body">
                      {(donor?.coefficient ?? 1).toFixed(2)}
                    </b>
                  </span>
                  <button
                    type="button"
                    disabled={exhausted}
                    onClick={() =>
                      applied
                        ? onRevert(proposal.recipientProductId)
                        : onApply(proposal.recipientProductId)
                    }
                    className={`ml-auto rounded-[7px] px-2.5 py-1.5 text-[11.5px] font-bold leading-[normal] ${
                      exhausted
                        ? "cursor-not-allowed border border-shade-3 bg-fill-2 text-muted"
                        : applied
                          ? "cursor-pointer border border-shade-3 bg-fill text-body hover:bg-fill-2"
                          : "cursor-pointer border-none bg-brand text-white hover:bg-brand-deep"
                    }`}
                  >
                    {applied
                      ? t("inventoryPage.subRevert")
                      : t("inventoryPage.subApply")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
};

export default StocktakeSubstitutionsPanel;
